<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-4">
      <span>
        Products Display
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-btn
          v-if="isEdit && !loading.displayProductsLoading"
          color="error"
          outlined
          rounded
          small
          :loading="loading.mainLoading"
          @click="onReset()">
          {{ $t('button.Reset') }}
        </v-btn>
        <CloneToCountries
          :brand="mapBrand"
          :countries="shippingCountries"
          :current-country="selectedCountry"
          :source-dirty="isEdit"
          :clone-fn="cloneProducts"
          :disabled="loading.mainLoading || !mapBrand || !shippingCountries.length" />
        <v-btn
          color="success"
          rounded
          small
          :loading="loading.mainLoading || loading.displayProductsLoading"
          :disabled="(!isEdit && !loading.displayProductsLoading) || !selectedCountry"
          @click="onSave()">
          {{ $t('button.Save') }}
        </v-btn>
      </div>
    </div>

    <v-row class="px-4">
      <v-col
        cols="12"
        md="7">
        <div class="card-container elevation-3">
          <v-row>
            <v-col
              cols="12"
              class="card-title">
              Brand to 📝 "DO"
            </v-col>
            <v-col cols="12">
              <v-tabs
                v-model="selectedBrand"
                color="secondary">
                <v-tab
                  v-for="brand in brands"
                  :key="brand">
                  {{ brand }}
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        cols="12"
        md="5">
        <div class="card-container elevation-3">
          <v-row>
            <v-col
              cols="12"
              class="card-title">
              Country to 🗺️ "DELIVER"
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="selectedCountry"
                placeholder="Country"
                color="secondary"
                prepend-inner-icon="mdi-flag-variant-outline"
                :items="shippingCountries"
                item-text="country"
                item-value="code"
                hide-details
                :loading="loading.mainLoading" />
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        v-show="selectedCountry"
        cols="12"
        md="7">
        <div class="card-container elevation-3">
          <v-row>
            <v-col cols="12">
              <div class="d-flex flex-row align-center justify-space-between">
                <span class="card-title">
                  Product to 🖥️ "SHOW"
                </span>
                <ProductImport
                  :loading="loading.importLoading"
                  :disabled="loading.displayProductsLoading || !selectedCountry"
                  :brand="mapBrand"
                  @import="onImport" />
              </div>
              <SearchByTags
                v-model="displayQuery"
                class="mt-2"
                append-icon="mdi-magnify"
                :tags="tags"
                :collections="collections"
                @on-search="fetchDisplayProducts(true)" />
            </v-col>
            <v-col cols="12">
              <v-row style="position: relative;">
                <Draggable
                  ref="displayProductsDraggable"
                  class="draggable-main-container"
                  :list="displayProducts"
                  group="products"
                  :style="{ display: !selectedCountry ? 'none' : 'flex' }">
                  <v-col
                    v-for="(product, index) in displayProducts"
                    :key="`product-${index}`"
                    cols="3"
                    xl="2">
                    <ProductBox
                      v-bind="product"
                      :index="index"
                      removeable
                      @remove="onRemoveProduct" />
                  </v-col>
                </Draggable>
                <div
                  v-if="!displayProducts.length && !loading.displayProductsLoading"
                  class="drag-drop-box">
                  <span class="font-weight-bold">
                    Drag & Drop
                  </span>
                  <span>
                    here to
                  </span>
                  <span class="font-weight-bold">
                    "SHOW"
                  </span>
                  <span>
                    the products
                  </span>
                </div>
                <div
                  v-else-if="!selectedCountry"
                  class="warning-box">
                  <span>
                    Please select
                  </span>
                  <span class="font-weight-bold">
                    "COUNTRY"
                  </span>
                  <span>
                    to show products
                  </span>
                </div>
              </v-row>
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        v-show="selectedCountry"
        cols="12"
        md="5">
        <div class="card-container elevation-3">
          <v-row>
            <v-col cols="12">
              <div class="d-flex flex-row align-center justify-space-between">
                <span class="card-title">
                  Products on 📦 "CONTAINER"
                </span>
                <v-btn
                  color="secondary"
                  rounded
                  small
                  :loading="loading.productsLoading"
                  :disabled="!selectedCountry || !products.length"
                  @click="onMoveAll()">
                  <v-icon small>
                    mdi-arrow-left
                  </v-icon>
                  <span class="ml-1">
                    {{ $t('button.MoveAll') }}  ({{ products.length }})
                  </span>
                </v-btn>
              </div>
              <SearchByTags
                v-model="productQuery"
                class="mt-2"
                append-icon="mdi-magnify"
                :tags="tags"
                :collections="collections"
                @on-search="fetchProducts(true)" />
            </v-col>
            <v-col cols="12">
              <v-row>
                <Draggable
                  ref="productsDraggable"
                  class="draggable-container"
                  :list="products"
                  :group="{ name: 'products', pull: 'clone', put: false }"
                  :style="{ display: !products.length && !loading.productsLoading ? 'none' : 'flex' }">
                  <v-col
                    v-for="(product, index) in products"
                    :key="`product-${index}`"
                    cols="4"
                    xl="3">
                    <ProductBox v-bind="product" />
                  </v-col>
                </Draggable>
                <v-col
                  v-if="!products.length && !loading.productsLoading"
                  cols="12"
                  class="d-flex justify-center align-center"
                  style="height: 224px;">
                  <span style="color: #9c9c9c;">
                    Oops! There is no product that matches your search criteria.
                  </span>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        v-if="!selectedCountry"
        cols="12">
        <div class="card-container elevation-3">
          <v-row>
            <v-col cols="12">
              <div class="warning-box">
                <span>
                  Please select
                </span>
                <span class="font-weight-bold">
                  "COUNTRY"
                </span>
                <span>
                  to show products
                </span>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Draggable from 'vuedraggable'
import SearchByTags from '@/components/SearchByTags.vue'
import ProductBox from '../components/ProductBox.vue'
import ProductImport from '../components/ProductImport.vue'
import CloneToCountries from '../components/CloneToCountries.vue'

import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import ProductProvider from '@/resources/ProductProvider'
import DistributorProductsProvider from '@/resources/DistributorProductsProvider'
import ExportCurrencyRatesProvider from '@/resources/ExportCurrencyRatesProvider'
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'
import ShippingCountries from '@/assets/json/ShippingCountries.json'

const ProductAttributeService = new ProductAttributeProvider()
const ProductService = new ProductProvider()
const DistributorProductsService = new DistributorProductsProvider()
const ExportCurrencyRatesService = new ExportCurrencyRatesProvider()
const ProductCollectionService = new ProductCollectionProvider()

const PLACEHOLDERS = Array.from({ length: 4 }, (_, i) => ({
  id: `loading-${i}`,
  loading: true
}))

export default {
  components: {
    Draggable,
    SearchByTags,
    ProductBox,
    ProductImport,
    CloneToCountries
  },
  data () {
    return {
      loading: {
        mainLoading: true,
        displayProductsLoading: false,
        productsLoading: false,
        importLoading: false
      },
      memoDisplayProducts: [],
      displayProducts: [],
      toHideProducts: [],
      products: [...PLACEHOLDERS],
      displayQuery: {
        search: '',
        tags: [],
        tagOperation: 'OR',
        collection: null,
        page: 1,
        limit: 30,
        totalPages: 0,
        sortOrder: 'asc',
        sortBy: 'priority',
        isOnWeb: true
      },
      productQuery: {
        search: '',
        tags: [],
        tagOperation: 'OR',
        collection: null,
        page: 1,
        itemsPerPage: 30,
        totalPages: 0,
        sortDesc: [true],
        sortBy: ['updatedAt']
      },
      brands: [],
      tags: [],
      collections: [],
      shippingCountries: [],
      selectedBrand: null,
      selectedCountry: null
    }
  },
  computed: {
    isEdit () {
      return JSON.stringify(this.displayProducts) !== JSON.stringify(this.memoDisplayProducts)
    },
    mapBrand () {
      return this.selectedBrand !== null ? this.brands[this.selectedBrand] : null
    }
  },
  watch: {
    selectedBrand () {
      this.refreshProducts()
    },
    selectedCountry () {
      this.productQuery = {
        ...this.productQuery,
        search: '',
        tags: [],
        tagOperation: 'OR',
        collection: null,
        page: 1
        // totalPages: 0
      }
      this.refreshProducts()
    },
    'loading.displayProductsLoading': {
      handler (val) {
        if (val) {
          this.displayProducts.push(...PLACEHOLDERS)
        } else {
          this.displayProducts = this.displayProducts.filter((p) => !p?.loading)
        }
      },
      deep: true
    },
    'loading.productsLoading': {
      handler (val) {
        if (val) {
          this.products.push(...PLACEHOLDERS)
        } else {
          this.products = this.products.filter((p) => !p?.loading)
        }
      },
      deep: true
    }
  },
  mounted () {
    this.initialize()

    this.$refs.displayProductsDraggable.$el.addEventListener('scroll', this.handleDisplayProductsDraggableScroll)
    this.$refs.productsDraggable.$el.addEventListener('scroll', this.handleProductsDraggableScroll)
  },
  beforeRouteLeave (to, from, next) {
    if (this.isEdit) {
      // eslint-disable-next-line no-alert
      const confirmLeave = window.confirm('You have unsaved changes. Do you really want to leave?')

      if (confirmLeave) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
  beforeDestroy () {
    this.$refs.displayProductsDraggable.$el.removeEventListener('scroll', this.handleDisplayProductsDraggableScroll)
    this.$refs.productsDraggable.$el.removeEventListener('scroll', this.handleProductsDraggableScroll)
  },
  methods: {
    async initialize () {
      try {
        this.loading.mainLoading = true

        await this.getBrands()
        await this.getTags()
        await this.getCollections()
        await this.getCountries()
      } catch (error) {
        console.error('initialize', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[INITIALIZE-ERROR]: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading.mainLoading = false
      }
    },
    async refreshProducts () {
      await this.fetchDisplayProducts(true)
      await this.fetchProducts(true)
    },
    handleProductsDraggableScroll (event) {
      const e = event.target
      const scrollY = e.scrollHeight - e.clientHeight - 100

      if (e.scrollTop >= scrollY && !this.loading.productsLoading && this.productQuery.page < this.productQuery.totalPages) {
        this.productQuery.page++
        this.fetchProducts()
      }
    },
    handleDisplayProductsDraggableScroll (event) {
      const e = event.target
      const scrollY = e.scrollHeight - e.clientHeight - 100

      if (e.scrollTop >= scrollY && !this.loading.displayProductsLoading && this.displayQuery.page < this.displayQuery.totalPages) {
        this.displayQuery.page++
        this.fetchDisplayProducts()
      }
    },
    async getBrands () {
      try {
        const { data } = await ProductAttributeService.getProductAttribute('brands', {
          limit: 9999,
          sortBy: 'createdAt',
          sortOrder: 'asc'
        })

        this.brands = [...data.results.map((re) => re.name)]
        this.selectedBrand = this.brands.length ? 0 : null
      } catch (error) {
        console.error('getBrands', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-BRANDS-ERROR]: ${error.message}`,
          type: 'error'
        })
      }
    },
    async getTags () {
      try {
        const { data } = await ProductAttributeService.getProductAttribute('tags', {
          page: 1,
          itemsPerPage: 9999999,
          sortBy: ['id'],
          sortDesc: [true]
        })

        this.tags = data.results.map((r) => ({
          id: r.id,
          name: r.name
        }))
      } catch (error) {
        console.error('getTags', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-TAGS-ERROR]: ${error.message}`,
          type: 'error'
        })
      }
    },
    async getCollections () {
      try {
        const { data } = await ProductCollectionService.getAll({
          page: 1,
          limit: 999,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        })

        this.collections = data.results.map((r) => ({
          id: r.id,
          name: r.name
        }))
      } catch (error) {
        console.error('getCollections', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-COLLECTIONS-ERROR]: ${error.message}`,
          type: 'error'
        })
      }
    },
    getShippingCountry (code) {
      return ShippingCountries.find((country) => country.code === code)?.country || code
    },
    async getCountries () {
      try {
        const { data } = await ExportCurrencyRatesService.getItems({})

        this.shippingCountries = data.map((r) => ({
          country: this.getShippingCountry(r.country),
          code: r.country
        }))
      } catch (error) {
        console.error('getCountries', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-COUNTRIES-ERROR]: ${error.message}`,
          type: 'error'
        })
      }
    },
    removeDuplicate (products) {
      const displayProductsIds = this.displayProducts.map((v) => v.id)

      return products.filter((p) => !displayProductsIds.includes(p.id))
    },
    async fetchProducts (isClear = false) {
      if (this.selectedBrand === null || this.selectedCountry === null) {
        return
      }

      try {
        this.loading.productsLoading = true

        if (isClear) {
          this.productQuery.page = 1
          this.products = [...PLACEHOLDERS]
        }

        const { data } = await ProductService.getProductsForSorting({
          ...this.productQuery,
          brand: this.brands[this.selectedBrand],
          collection: this.productQuery.collection || undefined
        })
        this.productQuery.totalPages = data.pages
        const cleanedProducts = this.removeDuplicate(data.results)
        this.products.push(...cleanedProducts)
      } catch (error) {
        console.error('fetchProducts', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-PRODUCT-ERROR]: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading.productsLoading = false
      }
    },
    cloneProducts (payload) {
      return DistributorProductsService.cloneItems(payload)
    },
    async fetchDisplayProducts (isClear = false) {
      if (this.selectedBrand === null || this.selectedCountry === null) {
        return
      }

      try {
        this.loading.displayProductsLoading = true

        if (isClear) {
          this.displayQuery.page = 1
          this.displayProducts = [...PLACEHOLDERS]
          this.memoDisplayProducts = []
        }

        const { data } = await DistributorProductsService.getItems({
          ...this.displayQuery,
          brand: this.brands[this.selectedBrand],
          country: this.selectedCountry,
          collection: this.displayQuery.collection || undefined
        })

        this.displayQuery.totalPages = data.pages

        const results = data.results.map((v) => ({
          ...v,
          id: v.productId
        }))

        this.displayProducts.push(...results)
        this.memoDisplayProducts.push(...results)
      } catch (error) {
        console.error('fetchDisplayProducts', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-DISPLAY-PRODUCT-ERROR]: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading.displayProductsLoading = false
      }
    },
    onReset () {
      this.displayProducts = [...this.memoDisplayProducts]
    },
    async onSave () {
      try {
        this.loading.displayProductsLoading = true

        const displayProductsIds = this.displayProducts.map((v) => v.id)
        const inactiveProducts = this.memoDisplayProducts
          .filter((v) => !displayProductsIds.includes(v.id))
          .map((v) => v.id)

        await DistributorProductsService.updateItems({
          brand: this.brands[this.selectedBrand],
          country: this.selectedCountry,
          activeProducts: [...new Set(displayProductsIds)],
          inactiveProducts: [...new Set(inactiveProducts)]
        })

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Save display products success.',
          type: 'success'
        })

        this.refreshProducts()
      } catch (error) {
        console.error('onSave', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading.displayProductsLoading = false
      }
    },
    async onImport (importProducts) {
      this.displayProducts.unshift(...importProducts)
    },
    onMoveAll () {
      this.displayProducts.push(...this.products)
    },
    onRemoveProduct (index) {
      this.displayProducts.splice(index, 1)
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
.draggable-container {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  min-height: 224px;
  max-height: 50vh;
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 2;
}
.draggable-main-container {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  min-height: 224px;
  max-height: 70vh;
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 2;
}
.drag-drop-box {
  position: absolute;
  width: 100%;
  height: 224px;
  border: 2px dashed #BEBEBE;
  border-radius: 0.625rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #9c9c9c;
  gap: 0.3rem;
  z-index: 1;
}
.warning-box {
  width: 100%;
  height: 224px;
  border: 2px dashed #BEBEBE;
  border-radius: 0.625rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #9c9c9c;
  gap: 0.3rem;
}
</style>
